import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../api/ApiProvider.js';
import { useActiveClassroom } from '../../context/ActiveClassroomProvider.js';
import { MONTH_NAMES, WEEKDAY_LABELS, buildMonthGrid, isoFor, shiftMonth } from './calendarMath.js';
import { DayCell } from './DayCell.js';
import { DayDetail } from './DayDetail.js';
import styles from './Calendar.module.css';

export function Calendar() {
  const api = useApi();
  const { active } = useActiveClassroom();

  const today = new Date();
  const [view, setView] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [selectedIso, setSelectedIso] = useState<string | null>(null);

  const cells = useMemo(() => buildMonthGrid(view.year, view.month), [view]);
  const monthFromIso = `${view.year}-${String(view.month + 1).padStart(2, '0')}-01`;
  const monthToIso = isoFor(new Date(view.year, view.month + 1, 0));

  const events = useQuery({
    queryKey: ['events', active?.id ?? 'none', monthFromIso, monthToIso],
    queryFn: () => api.calendar.listEvents(active!.id, monthFromIso, monthToIso),
    enabled: active !== null,
  });

  const birthdays = useQuery({
    queryKey: ['birthdays', active?.id ?? 'none'],
    queryFn: () => api.calendar.listBirthdays(active!.id),
    enabled: active !== null,
  });

  // The "school calendar" PDF link the teacher saved on the settings
  // page. Surfaced here as a pill in the header so it's actually
  // reachable from live mode — that's what "load it into the
  // calendar" means.
  const settings = useQuery({
    queryKey: ['classroom-settings', active?.id ?? 'none'],
    queryFn: () => api.home.getSettings(active!.id),
    enabled: active !== null,
  });
  const schoolCalUrl = settings.data?.schoolCalUrl?.trim() ?? '';
  const schoolCalTitle = settings.data?.schoolCalTitle?.trim() ?? '';
  const schoolCalIsHttp = /^https?:\/\//i.test(schoolCalUrl);

  // K-2 helper preference: drives the "Yesterday was…" / "Tomorrow
  // is…" lines in the DayDetail popup. Set on the Calendar settings
  // page under "K–2 helpers".
  const prefsQuery = useQuery({
    queryKey: ['preferences'],
    queryFn: () => api.preferences.get(),
  });
  const showYesterdayTomorrow = prefsQuery.data?.showYesterdayTomorrow ?? false;

  const eventsByIso = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const e of events.data ?? []) {
      const arr = map.get(e.eventDate) ?? [];
      arr.push(e.title);
      map.set(e.eventDate, arr);
    }
    return map;
  }, [events.data]);

  const birthdaysByMd = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const b of birthdays.data ?? []) {
      const key = `${String(b.month).padStart(2, '0')}-${String(b.day).padStart(2, '0')}`;
      const arr = map.get(key) ?? [];
      arr.push(b.name);
      map.set(key, arr);
    }
    return map;
  }, [birthdays.data]);

  const todayIso = isoFor(today);

  if (!active) {
    return (
      <section className={styles.empty} role="status">
        No classroom selected.
      </section>
    );
  }

  const selectedCell = selectedIso ? cells.find((c) => c.iso === selectedIso) : null;

  return (
    <section className={styles.calendar} data-testid="calendar-page">
      <div className={styles.card}>
        <header className={styles.header}>
          <h1 className={styles.monthTitle} data-testid="calendar-month">
            {MONTH_NAMES[view.month]} {view.year}
          </h1>
          <div className={styles.headerActions}>
            {schoolCalIsHttp ? (
              <a
                href={schoolCalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.schoolCalLink}
                data-testid="calendar-school-cal-link"
              >
                <span aria-hidden="true">📄</span>
                <span>{schoolCalTitle || 'School calendar'}</span>
              </a>
            ) : null}
            <div className={styles.monthNav}>
              <button
                type="button"
                onClick={() => setView((v) => shiftMonth(v, -1))}
                aria-label="Previous month"
                data-testid="calendar-prev"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={() => setView((v) => shiftMonth(v, 1))}
                aria-label="Next month"
                data-testid="calendar-next"
              >
                ›
              </button>
            </div>
          </div>
        </header>

        <div className={styles.weekHeader} aria-hidden="true">
          {WEEKDAY_LABELS.map((label) => (
            <div key={label} className={styles.weekHeaderCell}>
              {label}
            </div>
          ))}
        </div>

        <div className={styles.grid} data-testid="calendar-grid">
          {cells.map((cell) => {
            const md = cell.iso.slice(5);
            const events = eventsByIso.get(cell.iso) ?? [];
            const cellBirthdays = birthdaysByMd.get(md) ?? [];
            return (
              <DayCell
                key={cell.iso}
                cell={cell}
                isToday={cell.iso === todayIso}
                isSelected={cell.iso === selectedIso}
                events={events}
                birthdays={cellBirthdays}
                onSelect={() => setSelectedIso(cell.iso)}
              />
            );
          })}
        </div>
      </div>

      {selectedCell ? (
        <DayDetail
          date={selectedCell.date}
          iso={selectedCell.iso}
          events={eventsByIso.get(selectedCell.iso) ?? []}
          birthdays={birthdaysByMd.get(selectedCell.iso.slice(5)) ?? []}
          showYesterdayTomorrow={showYesterdayTomorrow}
          onClose={() => setSelectedIso(null)}
        />
      ) : null}
    </section>
  );
}
